import type { UserDataLoginRecord } from '@navio/nsdk';
import {
  tracking as core,
  type EventCatalog,
  type EventLogEntry,
  type EventProps,
  type EventShipper,
  type KnownEvent,
  type MixpanelOptions,
  MixpanelShipper,
  type OpenPanelOptions,
  OpenPanelShipper,
  type ShipperUser,
  toShipperUser,
  type TrackEnricher
} from '@navio/observability';
import { captureException } from '@sentry/react';

import { environment, telemetryEnabled } from '../constants/environment';
import { getLogTrackingEvents } from '../stores/DevSettingsStore';
import { resetBookingFunnel } from './bookingFunnel';
import { commonTrackingEvents } from './events';
import { createOpenPanelFunnels, useOpenPanelFunnel } from './funnels';

// Friendly destination labels. Shipper names map to the product the signal
// lands in — shared by the debug logger here and the dev-settings test card.
export const SHIPPER_LABELS: Record<string, string> = {
  faro: 'Grafana (Faro)',
  mixpanel: 'Mixpanel',
  openpanel: 'OpenPanel'
};

const OPENPANEL_ONLY = ['openpanel'] as const;
const formatDestinations = (shippers: string[]) =>
  shippers.length ? shippers.map((name) => SHIPPER_LABELS[name] ?? name).join(' + ') : 'no shippers ready';

const logTrackingEvent = (entry: EventLogEntry) => {
  if (!getLogTrackingEvents()) {
    return;
  }
  const to = `→ ${formatDestinations(entry.shippers)}`;
  switch (entry.type) {
    case 'track':
      // eslint-disable-next-line no-console -- opt-in dev tracking debug logger; console is its output
      console.info(`[tracking] event "${entry.name}" ${to}`, entry.props ?? {});
      break;
    case 'measure':
      // eslint-disable-next-line no-console -- opt-in dev tracking debug logger; console is its output
      console.info(`[tracking] measure "${entry.name}" = ${entry.value} ${to}`, entry.props ?? {});
      break;
    case 'identify':
      // eslint-disable-next-line no-console -- opt-in dev tracking debug logger; console is its output
      console.info(`[tracking] identify ${to}`, entry.user);
      break;
    case 'register':
      // eslint-disable-next-line no-console -- opt-in dev tracking debug logger; console is its output
      console.info(`[tracking] register super props ${to}`, entry.props);
      break;
    case 'unregister':
      // eslint-disable-next-line no-console -- opt-in dev tracking debug logger; console is its output
      console.info(`[tracking] unregister super props ${to}`, entry.keys);
      break;
    case 'setSessionProps':
      // eslint-disable-next-line no-console -- opt-in dev tracking debug logger; console is its output
      console.info(`[tracking] setSessionProps ${to}`, { set: entry.set, drop: entry.drop });
      break;
    case 'group':
      // eslint-disable-next-line no-console -- opt-in dev tracking debug logger; console is its output
      console.info(`[tracking] groups ${to}`, entry.groups);
      break;
    case 'clear':
      // eslint-disable-next-line no-console -- opt-in dev tracking debug logger; console is its output
      console.info(`[tracking] clear ${to}`);
      break;
    case 'screenView':
      // eslint-disable-next-line no-console -- opt-in dev tracking debug logger; console is its output
      console.info(`[tracking] screenView "${entry.path}" ${to}`, entry.props ?? {});
      break;
    case 'revenue':
      // eslint-disable-next-line no-console -- opt-in dev tracking debug logger; console is its output
      console.info(`[tracking] revenue ${entry.amount} ${to}`, entry.props ?? {});
      break;
  }
};

core.configure({
  onError: (error, ctx) => captureException(error, { tags: ctx }),
  onEvent: logTrackingEvent,
  onClear: resetBookingFunnel
});

const toAnalyticsUserType = (user: UserDataLoginRecord): string => {
  if (user.isSuperuser) {
    return 'superuser';
  }
  if (user.isStaff) {
    return 'staff';
  }
  if (user.isManager) {
    return 'manager';
  }
  if (user.isWorker) {
    return 'worker';
  }
  return 'customer';
};

type ProductShipperState = {
  mixpanelShipper?: MixpanelShipper;
  openPanelShipper?: OpenPanelShipper;
  mixpanelConfig?: { token: string; apiHost?: string; sessionReplay?: MixpanelOptions['sessionReplay'] };
  openPanelConfig?: {
    clientId: string;
    apiUrl: string;
    sessionReplay?: OpenPanelOptions['sessionReplay'];
    options?: Partial<OpenPanelOptions>;
  };
  forceShippersMemo?: EventShipper[];
};

const product: ProductShipperState = {};

const forceShippers = (): EventShipper[] => {
  if (product.forceShippersMemo) {
    return product.forceShippersMemo;
  }
  const shippers: EventShipper[] = [];

  if (product.openPanelConfig?.clientId && product.openPanelConfig?.apiUrl) {
    const baseOpts = product.openPanelConfig.options ?? {};
    shippers.push(
      telemetryEnabled && product.openPanelShipper
        ? product.openPanelShipper
        : new OpenPanelShipper({
            clientId: product.openPanelConfig.clientId,
            apiUrl: product.openPanelConfig.apiUrl,
            stub: false,
            sessionReplay: product.openPanelConfig.sessionReplay,
            ...baseOpts,
            trackScreenViews: false,
            trackOutgoingLinks: false,
            trackAttributes: false
          })
    );
  }

  if (product.mixpanelConfig?.token) {
    shippers.push(
      telemetryEnabled && product.mixpanelShipper
        ? product.mixpanelShipper
        : new MixpanelShipper({
            token: product.mixpanelConfig.token,
            apiHost: product.mixpanelConfig.apiHost,
            stub: false,
            sessionReplay: product.mixpanelConfig.sessionReplay
          })
    );
  }

  product.forceShippersMemo = shippers;
  return shippers;
};

const forceMixpanelShipper = (): MixpanelShipper | undefined =>
  forceShippers().find((shipper): shipper is MixpanelShipper => shipper.name === 'mixpanel');

function trackOpenPanel<E extends KnownEvent>(name: E, props: EventCatalog[E]): void;
function trackOpenPanel(name: string, props?: EventProps): void;
function trackOpenPanel(name: string, props?: EventProps) {
  core.track(name, props, { only: OPENPANEL_ONLY });
}

const identify = (user: UserDataLoginRecord) => {
  core.identify(toShipperUser(user));
  core.register({ userType: toAnalyticsUserType(user) });
};

export const tracking = {
  track: core.track.bind(core),
  measure: core.measure.bind(core),
  screenView: core.screenView.bind(core),
  // Prefer backend capture for confirmed money; kept for rare OpenPanel-only use.
  revenue: core.revenue.bind(core),
  getDeviceId: core.getDeviceId.bind(core),
  getReplays: core.getReplays.bind(core),
  register: core.register.bind(core),
  unregister: core.unregister.bind(core),
  registerBoot: core.registerBoot.bind(core),
  setGroups: core.setGroups.bind(core),
  setSessionProps: core.setSessionProps.bind(core),
  clear: core.clear.bind(core),
  registerShipper: core.registerShipper.bind(core),
  setTrackEnricher: (enrichTrack: TrackEnricher) => {
    core.configure({ enrichTrack });
  },
  trackOpenPanel,
  identify,
  trackUserIdentity: (user: UserDataLoginRecord) => {
    identify(user);
    core.track(commonTrackingEvents.userSignedUp);
  },
  trackUserLogin: (user: UserDataLoginRecord) => {
    identify(user);
    core.track(commonTrackingEvents.userLoggedIn);
  },
  initMixpanel: ({
    projectToken,
    apiHost,
    sessionReplay
  }: {
    projectToken: string;
    apiHost?: string;
    sessionReplay?: MixpanelOptions['sessionReplay'];
  }) => {
    product.mixpanelConfig = { token: projectToken, apiHost, sessionReplay };
    if (product.mixpanelShipper) {
      return;
    }
    product.mixpanelShipper = new MixpanelShipper({
      token: projectToken,
      apiHost,
      stub: !telemetryEnabled,
      sessionReplay
    });
    core.registerShipper(product.mixpanelShipper);
  },
  initOpenPanel: ({
    clientId,
    apiUrl,
    sessionReplay,
    ...options
  }: {
    clientId: string;
    apiUrl: string;
    sessionReplay?: OpenPanelOptions['sessionReplay'];
  } & Partial<OpenPanelOptions>) => {
    product.openPanelConfig = { clientId, apiUrl, sessionReplay, options };
    if (product.openPanelShipper) {
      return;
    }
    product.openPanelShipper = new OpenPanelShipper({
      clientId,
      apiUrl,
      stub: !telemetryEnabled,
      sessionReplay,
      ...options
    });
    core.registerShipper(product.openPanelShipper);
  },
  testTrackEvent: (name: string, props?: EventProps): string[] => {
    const shippers = forceShippers();
    const tagged = { ...props, test: true, environment };
    shippers.forEach((shipper) => shipper.trackEvent(name, tagged));
    return shippers.map((shipper) => shipper.name);
  },
  testIdentify: (user: ShipperUser): string[] => {
    const shippers = forceShippers();
    shippers.forEach((shipper) => shipper.identify(user));
    return shippers.map((shipper) => shipper.name);
  },
  testRegisterSuperProperty: (props: EventProps): string[] => {
    const shippers = forceShippers();
    shippers.forEach((shipper) => shipper.register?.({ ...props, environment }));
    return shippers.map((shipper) => shipper.name);
  },
  testStartSessionReplay: (): { mixpanelConfigured: boolean; mixpanelReplayId?: string } => {
    const mixpanel = forceMixpanelShipper();
    if (!mixpanel) {
      return { mixpanelConfigured: false };
    }
    mixpanel.startSessionRecording();
    const props = mixpanel.getSessionRecordingProperties();
    const replayId = props && '$mp_replay_id' in props ? (props.$mp_replay_id as string) : undefined;
    return { mixpanelConfigured: true, mixpanelReplayId: replayId };
  },
  testOpenPanelFunnel: (testId: string): string[] => {
    const openpanel = forceShippers().find((shipper) => shipper.name === 'openpanel');
    if (!openpanel) {
      return [];
    }
    const tag = { test: true, environment, testId, source: 'dev-settings' };
    openpanel.trackEvent(commonTrackingEvents.fleetScheduleStarted, {
      ...tag,
      numDepartments: 1
    });
    openpanel.trackEvent(commonTrackingEvents.fleetScheduleStepCompleted, {
      ...tag,
      step: 'organization'
    });
    openpanel.trackEvent(commonTrackingEvents.fleetScheduleCreated, {
      ...tag,
      scheduleId: 0,
      numSalesItems: 1
    });
    openpanel.trackEvent(commonTrackingEvents.fleetScheduleWizardCompleted, {
      ...tag,
      scheduleId: 0
    });
    return [openpanel.name];
  }
};

const { funnels, actions } = createOpenPanelFunnels(tracking.trackOpenPanel);

export const openPanelFunnels = funnels;
export const openPanelActions = actions;

export { trackBookingFlowContext, trackBookingFlowStarted } from './bookingFunnel';
export { trackBookingCreated, type BookingOrderMoney } from './bookingRevenue';
export { commonTrackingEvents } from './events';
export type { FleetBookingStep, FleetScheduleStep, ProposalAnalyticsProps, ProposalStep } from './events';
export { toFailureProps } from './funnels';
export { clearProposalContext, registerProposalContext, trackProposalStarted } from './proposalFunnel';
export { useOpenPanelFunnel };
